import React, { useState, useEffect, useMemo, useRef, useCallback, memo } from "react";
import { AppBar, Box, Typography, Popper, Grow, Paper, MenuList, MenuItem, ButtonBase, styled } from "@mui/material";
import { KeyboardArrowDown, LogoutOutlined, PersonOutlineRounded } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { navigations } from "app/constants/navigation";
import { fetchFilterDrops } from "app/services/api";
import { useMsal } from "@azure/msal-react";

/* ══════════════════════════════════════════════════════════
   COLOURS — the original navigation colours, unchanged
══════════════════════════════════════════════════════════ */
const FONT = `'Outfit', 'DM Sans', 'Segoe UI', sans-serif`;
const TOP_BG = "#3796a6";
const NAV_BG = "#112233";
const NAV_BG2 = "#1a2f3f";
const ACCENT = "#3796A6";
const ACCENT_LT = "#4ecee0";

const NAV_DIM = "rgba(255,255,255,0.72)";
const NAV_MUTED = "rgba(255,255,255,0.45)";

const DROP_BORDER = "#e5e7eb";
const DROP_TEXT = "#1f2937";
const DROP_ACTIVE = "#3796a6";
const DROP_HOVER = "rgba(55,150,166,0.08)";
const DROP_ACTIVE_BG = "rgba(55,150,166,0.12)";

const SIGN_BG = "#fff7ed";
const SIGN_BORDER = "#fdba74";
const SIGN_TEXT = "#c2410c";
const SIGN_HOVER = "#fef3c7";

// Two separate bars (never merge them): 26px teal + 28px navy (was 35 + 35). Pages fill the space below with
// flex/height:100%, so nothing depends on the exact total.
const TOP_H = 26; // teal logo / user bar
const NAV_H = 28; // navy menu bar
// Wide desktop screens (≥1600px) get roomier bars; laptops keep 26 + 28.
const DESKTOP_MQ = "@media (min-width: 1600px)";
const TOP_H_DESKTOP = 34;
const NAV_H_DESKTOP = 36;
const CLOSE_DELAY = 140; // ms — lets the pointer travel from the menu item into its dropdown

/* ══════════════════════════════════════════════════════════
   STYLED
══════════════════════════════════════════════════════════ */
const TopBar = styled(AppBar)({
  background: `linear-gradient(120deg, ${TOP_BG} 0%, #2a8fa0 55%, #1e7a8c 100%)`,
  height: TOP_H,
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0 12px",
  boxShadow: "inset 0 -1px 0 rgba(255,255,255,0.10)",
  position: "relative",
  zIndex: 1201,
  [DESKTOP_MQ]: {
    height: TOP_H_DESKTOP,
    padding: "0 16px",
    "& .top-logo": { height: "29px !important" },
    "& .top-user": { fontSize: "13.5px !important", maxWidth: "240px !important" },
    "& .top-user-icon": { fontSize: "17px !important" },
    "& .top-signout": { height: "24px !important", fontSize: "12px !important", padding: "0 10px !important" },
    "& .top-signout svg": { fontSize: "14px !important" },
  },
});

const NavBar = styled(AppBar)({
  background: `linear-gradient(90deg, ${NAV_BG} 0%, ${NAV_BG2} 100%)`,
  height: NAV_H,
  flexDirection: "row",
  alignItems: "center",
  padding: "0 4px",
  gap: 2,
  boxShadow: "0 2px 10px rgba(0,0,0,0.22)",
  position: "relative",
  zIndex: 1200,
  overflowX: "auto",
  overflowY: "hidden",
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": { display: "none" },
  [DESKTOP_MQ]: { height: NAV_H_DESKTOP, padding: "0 8px", gap: 4 },
});

const NavItem = styled(ButtonBase, { shouldForwardProp: (p) => p !== "active" && p !== "open" })(({ active, open }) => ({
  height: 22,
  padding: "0 9px",
  gap: 5,
  borderRadius: 7,
  fontFamily: FONT,
  fontSize: 12.5,
  fontWeight: active ? 600 : 500,
  letterSpacing: "0.01em",
  whiteSpace: "nowrap",
  flexShrink: 0,
  position: "relative",
  color: active || open ? "#fff" : NAV_DIM,
  background: active ? "rgba(255,255,255,0.12)" : open ? "rgba(255,255,255,0.07)" : "transparent",
  boxShadow: active ? "inset 0 0 0 1px rgba(255,255,255,0.10)" : "none",
  transition: "background 0.16s ease, color 0.16s ease",
  "& .nav-icon": { fontSize: 15, color: active ? ACCENT_LT : "inherit", opacity: active ? 1 : 0.8, transition: "color 0.16s" },
  // accent underline for the section you are in
  "&::after": {
    content: '""',
    position: "absolute",
    left: 10,
    right: 10,
    bottom: -3,
    height: 2,
    borderRadius: 2,
    background: `linear-gradient(90deg, ${ACCENT_LT}, ${ACCENT})`,
    transform: active ? "scaleX(1)" : "scaleX(0)",
    transition: "transform 0.2s cubic-bezier(.4,0,.2,1)",
  },
  "&:hover": { color: "#fff", background: active ? "rgba(255,255,255,0.14)" : "rgba(255,255,255,0.07)" },
  "&.Mui-focusVisible": { outline: `2px solid ${ACCENT_LT}`, outlineOffset: -2 },

  // Desktop: bigger, roomier items.
  [DESKTOP_MQ]: {
    height: 28, padding: "0 13px", gap: 7, fontSize: 13.5,
    "& .nav-icon": { fontSize: 17 },
    "&::after": { left: 13, right: 13, bottom: -4 },
  },
  // Laptops: tighten so every menu fits on one line without scrolling.
  "@media (max-width: 1439px)": { padding: "0 8px", gap: 5 },
  "@media (max-width: 1279px)": {
    padding: "0 7px",
    fontSize: 12,
    "& .nav-icon": { display: "none" }, // 1280px and below (e.g. 1366 @125%): text only
    "&::after": { left: 7, right: 7 },
  },
}));

const DropItem = styled(MenuItem, { shouldForwardProp: (p) => p !== "active" })(({ active }) => ({
  fontFamily: FONT,
  fontSize: 12.5,
  fontWeight: active ? 600 : 400,
  color: active ? DROP_ACTIVE : DROP_TEXT,
  background: active ? DROP_ACTIVE_BG : "transparent",
  minHeight: 25, // compact menu rows (was 32)
  padding: "0 10px 0 8px",
  borderRadius: 6,
  gap: 8,
  transition: "background 0.12s, color 0.12s",
  "&::before": {
    content: '""',
    width: 5,
    height: 5,
    borderRadius: "50%",
    flexShrink: 0,
    background: active ? DROP_ACTIVE : "transparent",
    transition: "background 0.12s",
  },
  "&:hover, &.Mui-focusVisible": { background: active ? DROP_ACTIVE_BG : DROP_HOVER, color: DROP_ACTIVE },
  "&:hover::before": { background: active ? DROP_ACTIVE : "rgba(55,150,166,0.45)" },
  [DESKTOP_MQ]: { fontSize: 13.5, minHeight: 31, padding: "0 14px 0 10px" },
}));

const readUser = () => {
  try {
    return JSON.parse(sessionStorage.getItem("userdata")) || {};
  } catch {
    return {};
  }
};

/* ══════════════════════════════════════════════════════════
   COMPONENT — permissions / navigation / logout logic unchanged
══════════════════════════════════════════════════════════ */
const HorizontalNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { instance } = useMsal();

  const user = useMemo(readUser, []);
  const username = user.username || "User";
  const [userModules, setUserModules] = useState([]);
  const [menu, setMenu] = useState({ name: null, anchor: null });
  const closeTimer = useRef();

  useEffect(() => {
    fetchFilterDrops("getuserrolespecificmodules", "", (data) => {
      const currentRole = data.find((r) => r.roleid === readUser().roleid);
      if (currentRole) setUserModules(currentRole.modules.split(",").map((m) => m.trim()));
    });
    return () => clearTimeout(closeTimer.current);
  }, []);

  const logout = () => {
    try { sessionStorage.removeItem("jobs-list-state:v1"); } catch { /* ignore */ } // remembered Jobs filters (JobList)
    const accounts = instance.getAllAccounts();
    if (accounts.length > 0) instance.setActiveAccount(null);
    window.location.href =
      "https://login.microsoftonline.com/common/oauth2/v2.0/logout?post_logout_redirect_uri=https://npgwfm.cyient.com/";
  };

  // Only the menus/pages this role may open (child `name` = permission key).
  const menus = useMemo(
    () =>
      navigations
        .map((parent) => {
          const children = parent.children.filter((c) => userModules.includes(c.name));
          return children.length > 0 ? { ...parent, children } : null;
        })
        .filter(Boolean),
    [userModules]
  );

  const openMenu = useCallback((name, anchor) => {
    clearTimeout(closeTimer.current);
    setMenu({ name, anchor });
  }, []);
  const closeNow = useCallback(() => {
    clearTimeout(closeTimer.current);
    setMenu({ name: null, anchor: null });
  }, []);
  const closeSoon = useCallback(() => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setMenu({ name: null, anchor: null }), CLOSE_DELAY);
  }, []);
  const keepOpen = useCallback(() => clearTimeout(closeTimer.current), []);

  const go = (path) => {
    closeNow();
    navigate(path);
  };

  const openParent = menus.find((m) => m.name === menu.name);

  return (
    <>
      {/* ════════ TOP BAR ════════ */}
      <TopBar position="static" elevation={0}>
        <Box component="img" className="top-logo" src="/assets/images/logo.png" alt="Northern Powergrid" sx={{ height: 23, display: "block", borderRadius: "2px" }} /> {/* 23px: readable, still fits the 26px bar */}

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {/* signed-in user: plain icon + name (no pill) */}
          <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }} title={username}>
            <PersonOutlineRounded className="top-user-icon" sx={{ fontSize: 15, color: "rgba(255,255,255,0.85)" }} />
            <Typography
              className="top-user"
              sx={{
                fontFamily: FONT,
                fontSize: 12.5,
                fontWeight: 600,
                color: "#fff",
                maxWidth: 160,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {username}
            </Typography>
          </Box>

          {/* sign out */}
          <ButtonBase
            onClick={logout}
            className="top-signout"
            sx={{
              height: 20,
              px: "8px",
              gap: "4px",
              borderRadius: "6px",
              fontFamily: FONT,
              fontSize: 11,
              fontWeight: 700,
              color: SIGN_TEXT,
              background: SIGN_BG,
              border: `1px solid ${SIGN_BORDER}`,
              transition: "background 0.16s, box-shadow 0.16s, border-color 0.16s",
              "&:hover": { background: SIGN_HOVER, borderColor: "#f97316", boxShadow: "0 2px 10px rgba(249,115,22,0.25)" },
            }}
          >
            <LogoutOutlined sx={{ fontSize: 13 }} />
            Sign Out
          </ButtonBase>
        </Box>
      </TopBar>

      {/* ════════ MENU BAR ════════ */}
      <NavBar position="static" elevation={0} component="nav">
        {menus.map((parent) => {
          const hasDropdown = parent.children.length > 1;
          const active = parent.children.some((c) => location.pathname === c.path);
          const open = menu.name === parent.name;
          const Icon = parent.icon;
          return (
            <NavItem
              key={parent.name}
              active={active ? 1 : 0}
              open={open ? 1 : 0}
              aria-haspopup={hasDropdown || undefined}
              aria-expanded={hasDropdown ? open : undefined}
              onMouseEnter={(e) => (hasDropdown ? openMenu(parent.name, e.currentTarget) : closeNow())}
              onMouseLeave={() => hasDropdown && closeSoon()}
              onClick={(e) => {
                if (!hasDropdown) go(parent.children[0].path);
                else if (open) closeNow();
                else openMenu(parent.name, e.currentTarget);
              }}
            >
              {Icon && <Icon className="nav-icon" />}
              {parent.label ?? parent.name}
              {hasDropdown && (
                <KeyboardArrowDown
                  sx={{
                    fontSize: 15,
                    ml: "-3px",
                    color: open ? "#fff" : NAV_MUTED,
                    transform: open ? "rotate(180deg)" : "none",
                    transition: "transform 0.2s cubic-bezier(.4,0,.2,1), color 0.15s",
                  }}
                />
              )}
            </NavItem>
          );
        })}
      </NavBar>

      {/* ════════ DROPDOWN (one shared, non-blocking popper) ════════ */}
      <Popper
        open={Boolean(openParent && menu.anchor)}
        anchorEl={menu.anchor}
        placement="bottom-start"
        transition
        sx={{ zIndex: 1300 }}
        modifiers={[{ name: "offset", options: { offset: [0, 2] } }]}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} timeout={140} style={{ transformOrigin: "top left" }}>
            <Paper
              onMouseEnter={keepOpen}
              onMouseLeave={closeSoon}
              elevation={0}
              sx={{
                mt: "2px",
                p: "3px",
                width: "max-content", // as wide as the longest item, no extra space
                borderRadius: "10px",
                border: `1px solid ${DROP_BORDER}`,
                borderTop: `2px solid ${ACCENT}`,
                boxShadow: "0 16px 40px rgba(15,23,42,0.14), 0 3px 10px rgba(15,23,42,0.06)",
              }}
            >
              <MenuList
                dense
                disablePadding
                autoFocusItem={false}
                onKeyDown={(e) => e.key === "Escape" && closeNow()}
                sx={{ display: "flex", flexDirection: "column", gap: 0 }}
              >
                {openParent?.children.map((child) => (
                  <DropItem key={child.name} active={location.pathname === child.path ? 1 : 0} onClick={() => go(child.path)}>
                    {child.label ?? child.name}
                  </DropItem>
                ))}
              </MenuList>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

export default memo(HorizontalNav);
